/**
 * Agent 相关的工具函数
 */

/**
 * 将 HTML 格式的 <think>/<thinking> 标签转换为 [THINK] 格式
 * 用于 AgentMessage 渲染前预处理，统一两种格式，使其都能被解析为思考状态展示
 */
export const normalizeThinkTags = (text: string): string => {
  return (
    text
      // 已闭合的 <think>/<thinking> 转换为 [THINK] 格式
      .replace(/<think(?:ing)?>([\s\S]*?)<\/think(?:ing)?>/gi, '[THINK] $1')
      // 未闭合的 <think>/<thinking>（流式输出中）转换为 [THINK] 格式
      .replace(/<think(?:ing)?>([\s\S]*)$/gi, '[THINK] $1')
      // 移除自闭合的 <think /> 或 <thinking /> 标签
      .replace(/<think(?:ing)?\s*\/>/gi, '')
  );
};

/**
 * 移除 <NO_ANSWER> 哨兵标记（agent 无答案时返回的占位，不应展示 / 复制 / 朗读给用户）
 * 覆盖全部形态：方括号 [NO_ANSWER]、开标签 <NO_ANSWER>、闭标签 </NO_ANSWER>、
 * 自闭合 <NO_ANSWER/> 或 <NO_ANSWER />
 * 由显示（AgentMessage）、复制（cleanAgentContent）、TTS（经 cleanAgentContent）统一复用，避免正则散落多份
 */
export const stripNoAnswerTag = (text: string): string => {
  return (
    text
      // 角括号形式：<NO_ANSWER> </NO_ANSWER> <NO_ANSWER/> <NO_ANSWER />
      .replace(/<\/?NO_ANSWER\s*\/?>/gi, '')
      // 方括号形式：[NO_ANSWER]
      .replace(/\[NO_ANSWER\]/gi, '')
  );
};

/**
 * 清理 Agent 特殊标签，只保留 ANSWER 内容
 * 用于复制功能，移除 PREAMBLE、THINK、TOOL_CALL、TOOL_RESPONSE 标签及其内容
 * 以及 HTML 格式的 <think>/<thinking> 标签
 */
export const cleanAgentContent = (text: string | null | undefined): string => {
  // 防御：后端对未回答/转人工/纯提问的消息可能返回 null，避免 .replace 报错
  if (!text) return '';
  const cleaned = text
    // 移除 HTML 格式的 <think>/<thinking> 标签及其内容（已闭合，多行支持）
    .replace(/<think(?:ing)?>([\s\S]*?)<\/think(?:ing)?>/gi, '')
    // 移除未闭合的 <think>/<thinking> 标签及其后续内容
    .replace(/<think(?:ing)?>([\s\S]*)$/gi, '')
    // 移除自闭合的 <think /> 或 <thinking /> 标签
    .replace(/<think(?:ing)?\s*\/>/gi, '')
    // 移除 CITATION 引用标签（自闭合和非自闭合形式）
    .replace(/<CITATION\b[^>]*?\/?>/gi, '')
    // 移除流式输出中不完整的 CITATION 标签（只有开头没有闭合 >），
    // 防止 TTS 朗读标签内的文件名，并保证清洗结果单调追加、分句索引不漂移
    // 注意：不能加 m 标志，$ 必须匹配整个字符串末尾而非行尾
    .replace(/<CITATION\b[^>]*$/gi, '')
    // 移除 [PREAMBLE] 及其内容（单行，以换行符为边界，避免误删其他标签内容）
    .replace(/\[PREAMBLE\]\n?[^\n]*\n?/g, '')
    // 移除 [THINK] 及其内容（直到下一个主要标签或结束）
    .replace(/\[THINK\][\s\S]*?(?=\[(?:ANSWER|TOOL_CALL|TOOL_RESPONSE)\]|$)/g, '')
    // 移除 [TOOL_CALL] 及其内容（直到下一个主要标签或结束）
    .replace(/\[TOOL_CALL\][\s\S]*?(?=\[(?:THINK|ANSWER|TOOL_RESPONSE)\]|$)/g, '')
    // 移除 [TOOL_RESPONSE] 及其内容（直到下一个主要标签或结束）
    .replace(/\[TOOL_RESPONSE\][\s\S]*?(?=\[(?:THINK|ANSWER|TOOL_CALL)\]|$)/g, '')
    // 移除 [ANSWER] 标签本身，保留其后的内容
    .replace(/\[ANSWER\]\s*/g, '');

  // 复用 stripNoAnswerTag 移除 <NO_ANSWER> 哨兵标记（含方括号形式），统一清理逻辑
  return stripNoAnswerTag(cleaned).trim();
};
